import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { JiraService } from '../../../services/jira/jira.service';
import { JiraPriority, JiraIssueRequest } from '../../../models/jira.models';
import { ToastService } from '../../../utils/toast.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../../auth.service';
import jwt_decode from 'jwt-decode';

@Component({
  selector: 'app-bug-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  templateUrl: './bug-report-modal.component.html',
  styleUrl: './bug-report-modal.component.scss'
})
export class BugReportModalComponent implements OnInit {
  bugForm!: FormGroup;
  priorities: JiraPriority[] = [];
  isSubmitting = false;
  selectedFiles: File[] = [];
  maxFiles = 5;
  maxFileSize = 10 * 1024 * 1024; // 10MB
  currentUser: any = null;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private jiraService: JiraService,
    private toastService: ToastService,
    private cookieService: CookieService,
    private authService: AuthService
  ) {
    this.priorities = this.jiraService.getPriorities();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadCurrentUser();
  }

  private initForm(): void {
    this.bugForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      priority: [JiraPriority.MEDIUM, Validators.required]
    });
  }

  private loadCurrentUser(): void {
    try {
      const token = this.cookieService.get('gr_access');
      if (token) {
        const decoded: any = jwt_decode(token);
        const userId = decoded.user_id || decoded.id;

        if (userId) {
          this.authService.getMe(userId).subscribe({
            next: (user) => {
              this.currentUser = user;
            },
            error: (error) => {
              console.error('Error loading user data:', error);
              this.toastService.showNotificationModal(
                'Error',
                'No se pudo cargar la información del usuario'
              );
            }
          });
        }
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  private getUserInfo(): { name: string; email: string } {
    if (this.currentUser) {
      return {
        name: this.currentUser.name || this.currentUser.username || this.currentUser.first_name + ' ' + this.currentUser.last_name || '',
        email: this.currentUser.email || ''
      };
    }
    return { name: '', email: '' };
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;

    if (files) {
      const filesArray = Array.from(files);

      // Validar número máximo de archivos
      if (this.selectedFiles.length + filesArray.length > this.maxFiles) {
        this.toastService.showNotificationModal(
          'Error',
          `Solo puedes adjuntar hasta ${this.maxFiles} archivos`
        );
        return;
      }

      // Validar tamaño de archivos
      for (const file of filesArray) {
        if (file.size > this.maxFileSize) {
          this.toastService.showNotificationModal(
            'Error',
            `El archivo ${file.name} excede el tamaño máximo de 10MB`
          );
          return;
        }
      }

      this.selectedFiles.push(...filesArray);
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onSubmit(): void {
    if (this.bugForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      const formValue = this.bugForm.value;
      const userInfo = this.getUserInfo();

      const issueRequest: JiraIssueRequest = {
        title: formValue.title,
        description: formValue.description,
        priority: formValue.priority,
        reporterName: userInfo.name,
        reporterEmail: userInfo.email,
        attachments: this.selectedFiles.length > 0 ? this.selectedFiles : undefined
      };

      this.jiraService.createBugIssue(issueRequest).subscribe({
        next: (response) => {
          this.toastService.showToastSuccess(
            'Bug reportado exitosamente', `Ticket creado: ${response.key}`
          );
          this.activeModal.close(response);
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error creating bug report:', error);
          this.toastService.showNotificationModal(
            'Error al reportar el bug', 'Por favor intenta nuevamente'
          );
          this.isSubmitting = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.bugForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  public get title() { return this.bugForm.get('title'); }
  public get description() { return this.bugForm.get('description'); }
  public get priority() { return this.bugForm.get('priority'); }
}
